import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LoginPage from "@/pages/LoginPage";
import AuthCallbackPage from "@/pages/AuthCallbackPage";
import { AdminLayout, CoordinatorLayout } from "@/pages/DashboardLayout";
import SectorsPage from "@/pages/admin/SectorsPage";
import UsersPage from "@/pages/admin/UsersPage";
import SettingsPage from "@/pages/admin/SettingsPage";
import PluginPage from "@/pages/admin/PluginPage";
import DocumentsPage from "@/pages/DocumentsPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import CoordinatorUsersPage from "@/pages/coordinator/CoordinatorUsersPage";
import PreviewPage from "@/pages/coordinator/PreviewPage";
import ChatbotPublicPage from "@/pages/ChatbotPublicPage";
import { Loader2 } from "lucide-react";

function RequireAuth({ role, children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#1e5a8e]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    return <Navigate to={user.role === "super_admin" ? "/admin/sectors" : "/coordinator/documents"} replace />;
  }
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "super_admin" ? "/admin/sectors" : "/coordinator/documents"} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/chatbot/:slug" element={<ChatbotPublicPage />} />

          <Route
            path="/admin"
            element={
              <RequireAuth role="super_admin">
                <AdminLayout />
              </RequireAuth>
            }
          >
            <Route index element={<Navigate to="sectors" replace />} />
            <Route path="sectors" element={<SectorsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="plugin" element={<PluginPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          <Route
            path="/coordinator"
            element={
              <RequireAuth role="coordinator">
                <CoordinatorLayout />
              </RequireAuth>
            }
          >
            <Route index element={<Navigate to="documents" replace />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="users" element={<CoordinatorUsersPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="preview" element={<PreviewPage />} />
          </Route>

          <Route path="/" element={<RootRedirect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
